import { Ref } from 'vue';
import { useStorage } from '@vueuse/core';
import { DEBUG_NAMESPACE, useDebug } from '@/composables/useDebug';
import { useToast } from '@/composables/useToast';
import { useItemActions } from '@/modules/viewer/composables/useItemActions';

const { debug } = useDebug(DEBUG_NAMESPACE.CALLBACKS_STORAGE);

export enum CallbackId {
  OPEN_TOAST_COPIED_SHARE_SETTINGS = 'OPEN_TOAST_COPIED_SHARE_SETTINGS',
}

export const useCallbacksStorage = () => {
  const storageQueue: Ref<{ key: string; callbackId: CallbackId }[]> = useStorage('viewer-callbacks-storage', []);

  const set = (key: string, callbackId: CallbackId) => {
    try {
      storageQueue.value.push({ key, callbackId });
      debug(`[StoredToast] Stored: ${key}: ${callbackId}`);
    } catch (e) {
      debug(e);
    }
  };

  const run = (key: string) => {
    storageQueue.value
      .filter(t => t.key === key)
      .forEach(({ callbackId }) => {
        runCallbackById(callbackId);
        debug(`[StoredToast] Run callback for key: ${key} with callbackId: ${callbackId}`);
      });
    remove(key);
  };

  const remove = (key: string) => {
    storageQueue.value = storageQueue.value.filter(t => t.key !== key);
    debug(`[StoredToast] Remove stored callbacks for key: ${key}`);
  };

  const runCallbackById = (callbackId: CallbackId) => {
    const { success } = useToast();
    const { openShareModalAction } = useItemActions();

    switch (callbackId) {
      case CallbackId.OPEN_TOAST_COPIED_SHARE_SETTINGS:
        return success('We have applied the same access settings as those in the original file.', {
          actions: [{ title: 'Modify settings', handler: () => openShareModalAction() }],
        });
      default:
        debug(`[StoredToast] No handler callback defined for: ${callbackId}`);
    }
  };

  return { set, remove, run };
};
